import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-regular-svg-icons/faMagnifyingGlass';
import { useTranslation } from 'react-i18next';
import { Card } from 'Card';
import '../../locales/studentNetworkV2/studentNetworkV2-en.json';

type SearchRow = {
    label: string;
    value: string;
    buttonColor: string;
    onSearch: () => void;
};

function PrefilledSearchRow({ label, value, buttonColor, onSearch }: SearchRow) {
    return (
        <div className="flex items-stretch gap-2">
            {/* Prefilled search box — Sketch: 274x56, #f6f7f9 bg, 12px corners */}
            <div className="flex min-h-14 flex-1 flex-col justify-center rounded-[12px] bg-slate-grey-50 px-3 py-1.5">
                <span className="text-sm text-slate-grey">{label}</span>
                <span className="text-base text-black">{value}</span>
            </div>
            {/* Search button — Sketch: 56x56, pastel color per row */}
            <button
                type="button"
                onClick={onSearch}
                className="flex w-14 shrink-0 items-center justify-center rounded-[12px] transition-[filter] duration-200 hover:brightness-[0.96]"
                style={{ backgroundColor: buttonColor }}
            >
                <FontAwesomeIcon icon={faMagnifyingGlass} className="text-base text-slate-grey" />
            </button>
        </div>
    );
}

type FindMoreSearchType = 'location' | 'company' | 'industry' | 'school' | 'program';

type FindMoreStudentsSidebarProps = {
    location?: string;
    company?: string;
    industry?: string;
    school?: string;
    program?: string;
    onSearch?: (type: FindMoreSearchType, value: string) => void;
};

// Pastel button colors per row from Sketch mockup
const ROW_COLORS = {
    location: '#ffe3e1',
    company: '#fffbc5',
    industry: '#d8ffe0',
    school: '#c9fef1',
    program: '#dae3ff',
};

export function FindMoreStudentsSidebar({
    location,
    company,
    industry,
    school,
    program,
    onSearch,
}: FindMoreStudentsSidebarProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'studentNetworkV2.studentNetworkV2.findMore' });

    const search = (type: FindMoreSearchType, value: string) => onSearch?.(type, value);

    const rows: SearchRow[] = [
        location && {
            label: t('fromLocation'),
            value: location,
            buttonColor: ROW_COLORS.location,
            onSearch: () => search('location', location),
        },
        company && {
            label: t('workedAtCompany'),
            value: company,
            buttonColor: ROW_COLORS.company,
            onSearch: () => search('company', company),
        },
        industry && {
            label: t('fromIndustry'),
            value: industry,
            buttonColor: ROW_COLORS.industry,
            onSearch: () => search('industry', industry),
        },
        school && {
            label: t('attendedSchool'),
            value: school,
            buttonColor: ROW_COLORS.school,
            onSearch: () => search('school', school),
        },
        program && {
            label: t('enrolledInProgram'),
            value: program,
            buttonColor: ROW_COLORS.program,
            onSearch: () => search('program', program),
        },
    ].filter(Boolean) as SearchRow[];

    if (rows.length === 0) return null;

    return (
        <Card
            padding={0}
            radius={8}
            className="sticky top-0 hidden w-[350px] shrink-0 self-start min-[1200px]:block min-[1400px]:w-[410px]"
        >
            <div className="p-7.5">
                <h3 className="mb-5 text-lg font-bold text-black">{t('title')}</h3>
                <div className="flex flex-col gap-5">
                    {rows.map(row => (
                        <PrefilledSearchRow key={row.label} {...row} />
                    ))}
                </div>
            </div>
        </Card>
    );
}
